import Search from '@mui/icons-material/Search';
import { Box, CircularProgress, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import type { TableCellProps } from '@mui/material/TableCell';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Section } from '../../ui/Section';
import { Pill } from '../Pill';
import { EmptyTable } from '../EmptyTable';
import CustomPagination from '../../forms/Pagination';
import { TableLoading } from '../../forms/TableLoading';

export interface TableColumn<T> {
  id: string;
  label: ReactNode;
  /** Direct row property for simple text/number cells. */
  field?: keyof T;
  align?: TableCellProps['align'];
  sortable?: boolean;
  /** API/domain sort key. Defaults to `id` when omitted. */
  sortKey?: string;
  /** Custom cell content for formatting, links, status chips or actions. */
  render?: (row: T, rowIndex: number) => ReactNode;
  /** Fixed column width; pair with `tableSx={{ tableLayout: 'fixed' }}` so widths hold across pages. */
  width?: number | string;
}

export type TableSortDirection = 'asc' | 'desc';

export interface TableSectionProps<T> {
  title: ReactNode;
  columns: readonly TableColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T, rowIndex: number) => string | number;
  onRowClick?: (row: T, rowIndex: number) => void;
  recordCount?: string | number;
  recordCountLabel?: string;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  actions?: ReactNode;
  page?: number;
  maxPages?: number;
  onPageChange?: (page: number) => void;
  sortBy?: string;
  sortDirection?: TableSortDirection;
  onSortChange?: (sortBy: string, sortDirection: TableSortDirection) => void;
  paginationBreakpoint?: number;
  loading?: boolean;
  /** Keeps existing rows visible while a subsequent request refreshes the table. */
  loadingOverlay?: boolean;
  emptyMessage?: ReactNode;
  /** Optional full empty state rendered inside the standardized table shell. */
  emptyContent?: ReactNode;
  /**
   * Optional custom body for editable, matrix or comparison tables. The shared
   * header, table shell and pagination remain standardized.
   */
  renderBody?: (rows: readonly T[]) => ReactNode;
  tableSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
}

export const TableSection = <T,>({
  title,
  columns,
  rows,
  getRowId,
  onRowClick,
  recordCount,
  recordCountLabel,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  actions,
  page,
  maxPages,
  onPageChange,
  sortBy,
  sortDirection = 'asc',
  onSortChange,
  paginationBreakpoint = 120000,
  loading = false,
  loadingOverlay = false,
  emptyMessage,
  emptyContent,
  renderBody,
  tableSx,
  sx,
}: TableSectionProps<T>) => {
  const { t } = useTranslation('common');
  const displayedRecordCount = recordCount ?? rows.length;
  const hasPagination = page !== undefined
    && maxPages !== undefined
    && onPageChange !== undefined;

  const renderCell = (column: TableColumn<T>, row: T, rowIndex: number): ReactNode => {
    if (column.render) {
      return column.render(row, rowIndex);
    }

    if (column.field !== undefined) {
      return row[column.field] as ReactNode;
    }

    return null;
  };

  const handleSortChange = (column: TableColumn<T>) => {
    if (!column.sortable || !onSortChange) return;

    const nextSortKey = column.sortKey ?? column.id;
    const nextDirection = sortBy === nextSortKey && sortDirection === 'asc'
      ? 'desc'
      : 'asc';

    onSortChange(nextSortKey, nextDirection);
  };

  const defaultEmptyContent = (
    <EmptyTable
      text={typeof title === 'string' ? title : t('items')}
      onClear={() => undefined}
      onAdd={() => undefined}
      addItemText=""
      onAddIsCreating={false}
      showClear={false}
      showOnAdd={false}
      withPaginationShown={false}
    />
  );

  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        mb: 5,
        ...sx,
      }}
    >
      <Section style={{ boxShadow: 'none', mb: 0, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3,
            flexWrap: 'wrap',
            p: 5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Typography
              variant="textMD"
              fontWeight={600}
            >
              {title}
            </Typography>
            <Pill
              text={recordCountLabel
                ? `${displayedRecordCount} ${recordCountLabel}`
                : String(displayedRecordCount)}
            />
          </Box>

          {(onSearchChange !== undefined || actions !== undefined) && (
            <Box
              sx={{
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                flexWrap: 'wrap',
                gap: 2,
                minWidth: 0,
              }}
            >
              {onSearchChange !== undefined && (
                <TextField
                  value={searchValue ?? ''}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={searchPlaceholder ?? t('search')}
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 360 },
                    mb: 0,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    'aria-label': searchPlaceholder ?? t('search'),
                  }}
                />
              )}
              {actions}
            </Box>
          )}
        </Box>

        {loading ? (
          <TableLoading
            headerHeight={45}
            bodyRowHeight={40}
            minTableWidth={0}
          />
        ) : (
          <TableContainer>
            <Table sx={{ width: '100%', ...tableSx }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={column.width !== undefined ? { width: column.width } : undefined}
                    >
                      {column.sortable && onSortChange ? (
                        <TableSortLabel
                          active={sortBy === (column.sortKey ?? column.id)}
                          direction={sortBy === (column.sortKey ?? column.id) ? sortDirection : 'asc'}
                          onClick={() => handleSortChange(column)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {rows.length === 0 && (emptyContent || !emptyMessage) ? (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(columns.length, 1)}
                      sx={{ p: 0, borderBottom: 0 }}
                    >
                      {emptyContent ?? defaultEmptyContent}
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : renderBody ? (
                renderBody(rows)
              ) : (
                <TableBody>
                  {rows.length > 0 ? rows.map((row, rowIndex) => (
                    <TableRow
                      key={getRowId(row, rowIndex)}
                      onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                      sx={onRowClick ? {
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      } : undefined}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                        >
                          {renderCell(column, row, rowIndex)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(columns.length, 1)}
                        align="center"
                      >
                        {emptyMessage ?? (
                          <Typography
                            variant="textSM"
                            color="text.secondary"
                          >
                            {t('noData')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        )}

        {hasPagination && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CustomPagination
              page={page}
              setPage={onPageChange}
              maxPages={maxPages}
              breakpointForChangeDisplay={paginationBreakpoint}
              useNoDataMessage={false}
            />
          </Box>
        )}

        {loadingOverlay && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.62)',
            }}
            aria-label={t('loading')}
            role="status"
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </Section>
    </Box>
  );
};
